import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Suspense,
  lazy,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useContextStore } from "@/store/context";
import Layout from "@/app/Layout";
import NotificationProvider, { toast } from "@/providers/NotificationProvider";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useAuth } from "@/providers/AuthProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { getConsoleUrl } from "@/config/runtime";
import {
  openExternalUrl,
} from "@/integrations/tauri/runtimeConfig";
import {
  installDesktopUpdate,
  onDesktopUpdateAvailable,
} from "@/integrations/tauri/updater";
import BrandLogo from "@/components/shell/BrandLogo";
import LanguageSwitcher from "@/components/shell/LanguageSwitcher";
import ThemeSwitcher from "@/components/shell/ThemeSwitcher";
import EnrollmentScreen from "@/components/auth/EnrollmentScreen";
import HankoAuthElement from "@/components/auth/HankoAuthElement";
import { standaloneSetupAPI } from "@/services/api/standaloneSetup";
import type { StandaloneSetupStatus } from "@/services/api/standaloneSetup";
import { llmAPI } from "@/services/api/llm";
import type { OrganizationLLMResolvedConfig } from "@/services/api/runtimeTypes";
import {
  getStoredAccessMode,
  inferAccessModeFromServer,
  setStoredAccessMode,
} from "@/services/accessMode";
import { needsStandaloneOnboarding } from "@/app/onboardingRoute";
import {
  getHankoElementsLanguage,
  registerHankoElements,
} from "@/services/hankoAuth";
import { resolvePlateformeHomeRoute } from "@/utils/studioHomeRoute";
import { routePosture } from "@/utils/posture";
import { legacyRouteTarget } from "@/utils/legacyRoutes";


const CatalogPage = lazy(() => import("@/pages/operate/CatalogPage"));
const ChatPage = lazy(() => import("@/pages/operate/ChatPage"));
const HistoryPage = lazy(() => import("@/pages/operate/HistoryPage"));
const TravailInboxPage = lazy(
  () => import("@/pages/operate/TravailInboxPage"),
);
const TravailActivityPage = lazy(
  () => import("@/pages/operate/TravailActivityPage"),
);
const WelcomePage = lazy(() => import("@/pages/WelcomePage"));
const AgentSpecStudioChatPage = lazy(
  () => import("@/pages/calisto/AgentSpecStudioChatPage"),
);
const AgentSpecPage = lazy(() => import("@/pages/build/AgentSpecPage"));
const AccessPage = lazy(() => import("@/pages/build/AccessPage"));
const ApiKeysPage = lazy(() => import("@/pages/build/ApiKeysPage"));
const GatesPage = lazy(() => import("@/pages/organization/GatesPage"));
const MembersPage = lazy(() => import("@/pages/organization/MembersPage"));
const UsagePage = lazy(() => import("@/pages/organization/UsagePage"));
const AccessMapPage = lazy(() => import("@/pages/organization/AccessMapPage"));
const VaultPage = lazy(() => import("@/pages/organization/VaultPage"));
const MyAccessPage = lazy(() => import("@/pages/operate/MyAccessPage"));
const ImportExportPage = lazy(() => import("@/pages/build/ImportExportPage"));
const LLMPage = lazy(() => import("@/pages/build/LLMPage"));
const MCPPage = lazy(() => import("@/pages/build/MCPPage"));
const MessagingPage = lazy(() => import("@/pages/build/MessagingPage"));
const MailCatcherPage = lazy(() => import("@/pages/build/MailCatcherPage"));
const SpacesPage = lazy(() => import("@/pages/build/SpacesPage"));
const DataPage = lazy(() => import("@/pages/build/DataPage"));
const VariablesPage = lazy(() => import("@/pages/build/VariablesPage"));
const IntegrationsPage = lazy(() => import("@/pages/build/IntegrationsPage"));
const ApplicationWebPage = lazy(
  () => import("@/pages/build/ApplicationWebPage"),
);
const RunSpacesPage = lazy(() => import("@/pages/operate/RunSpacesPage"));
const ComposePage = lazy(() => import("@/pages/compose/ComposePage"));

function AuthGate() {
  const { t, i18n } = useTranslation();
  const {
    authError,
    authMode,
    defaultLocalUser,
    loading,
    isAuthenticated,
    localUsers,
    login,
  } = useAuth();
  const consoleUrl = getConsoleUrl();
  const [selectedLocalUser, setSelectedLocalUser] = useState("");
  const [hankoError, setHankoError] = useState<string | null>(null);
  const hankoLanguage = getHankoElementsLanguage(
    i18n.resolvedLanguage || i18n.language,
  );

  useEffect(() => {
    if (authMode !== "local") {
      return;
    }
    if (selectedLocalUser) {
      return;
    }
    setSelectedLocalUser(defaultLocalUser || localUsers[0]?.email || "");
  }, [authMode, defaultLocalUser, localUsers, selectedLocalUser]);

  useEffect(() => {
    if (authMode !== "hanko") {
      return;
    }
    registerHankoElements().catch((err) => {
      setHankoError(err instanceof Error ? err.message : t("auth.signInError"));
    });
  }, [authMode, t]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t("auth.checking")}
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  if (authMode === "hanko") {
    return (
      <div className="flex h-screen items-center justify-center bg-background px-6">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="space-y-3">
              <BrandLogo className="max-w-[12rem]" />
              <h1 className="text-2xl font-semibold">AgentMaurice OS</h1>
            </div>
            <div className="flex items-center gap-3">
              <ThemeSwitcher compact />
              <LanguageSwitcher compact />
            </div>
          </div>

          {(hankoError || authError) && (
            <div className="mt-4 rounded-lg border border-error/30 bg-error-soft px-3 py-2 text-sm text-error">
              {hankoError || authError}
            </div>
          )}
          <div className="mt-5">
            <HankoAuthElement lang={hankoLanguage} />
          </div>

          <p className="mt-5 text-center text-sm text-muted-foreground">
            {t("auth.noAccountYet")}{" "}
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => void openExternalUrl(consoleUrl)}
            >
              {t("auth.createAccount")}
            </button>
          </p>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {t("auth.createAccountOnConsoleHint")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="space-y-3">
            <BrandLogo className="max-w-[12rem]" />
            <h1 className="text-2xl font-semibold">AgentMaurice OS</h1>
          </div>
          <div className="flex items-center gap-3">
            <ThemeSwitcher compact />
            <LanguageSwitcher compact />
          </div>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("auth.required")}
        </p>
        {authMode === "local" && (
          <div className="mt-4 space-y-3">
            <div className="rounded-lg border border-success/30 bg-success-soft px-3 py-2 text-sm text-success">
              {t("auth.localModeEnabled")}
            </div>
            {localUsers.length > 0 && (
              <label className="block text-sm">
                <span className="mb-1 block text-muted-foreground">
                  {t("auth.localAccount")}
                </span>
                <select
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={selectedLocalUser}
                  onChange={(event) => setSelectedLocalUser(event.target.value)}
                >
                  {localUsers.map((account) => (
                    <option key={account.email} value={account.email}>
                      {account.name || account.email} ({account.role})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        {authError && (
          <div className="mt-4 rounded-lg border border-error/30 bg-error-soft px-3 py-2 text-sm text-error">
            {authError}
          </div>
        )}
        <div className="mt-6 flex gap-3">
          <Button
            onClick={() =>
              void login(
                authMode === "local"
                  ? selectedLocalUser || undefined
                  : undefined,
              )
            }
            className="flex-1"
          >
            {authMode === "local" ? t("auth.localSignIn") : t("auth.signIn")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RequireAccess({
  when,
  children,
  redirectTo = "/run/chat",
  fallback,
}: {
  when: boolean;
  children: ReactElement;
  redirectTo?: string;
  fallback?: ReactElement;
}) {
  if (!when) {
    if (fallback) {
      return fallback;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

function AccessBlockedPage({
  title,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
}: {
  title: string;
  description: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}) {
  const navigate = useNavigate();

  return (
    <div className="min-h-full px-6 py-8">
      <div className="mx-auto flex min-h-[calc(100vh-8rem)] max-w-4xl items-center justify-center">
        <Card className="w-full rounded-2xl border-border bg-card p-8">
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-[13px] font-semibold text-foreground">
                Build
              </p>
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
              <p className="max-w-2xl text-sm leading-7 text-muted-foreground">
                {description}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate(primaryHref)}>
                {primaryLabel}
              </Button>
              {secondaryHref && secondaryLabel ? (
                <Button
                  variant="outline"
                  onClick={() => navigate(secondaryHref)}
                >
                  {secondaryLabel}
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function DesktopUpdateNotifier() {
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    let cleanup: (() => void) | null = null;

    void onDesktopUpdateAvailable(({ version }) => {
      const toastId = toast(
        (toastState) => (
          <div className="flex max-w-sm items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {t("desktopUpdate.availableTitle")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("desktopUpdate.availableDescription", {
                  version: version || t("desktopUpdate.genericVersion"),
                })}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                toast.dismiss(toastState.id);
                const loadingId = toast.loading(t("desktopUpdate.installing"));
                void installDesktopUpdate()
                  .then(() => {
                    toast.dismiss(loadingId);
                  })
                  .catch((error) => {
                    toast.dismiss(loadingId);
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t("desktopUpdate.installError"),
                    );
                  });
              }}
            >
              {t("desktopUpdate.installAction")}
            </Button>
          </div>
        ),
        { duration: Infinity, id: `desktop-update-${version || "latest"}` },
      );
      return toastId;
    }).then((unlisten) => {
      if (!active) {
        unlisten?.();
        return;
      }
      cleanup = unlisten;
    });

    return () => {
      active = false;
      cleanup?.();
    };
  }, [t]);

  return null;
}

function StandaloneWelcomeBootstrap() {
  const location = useLocation();
  const navigate = useNavigate();
  const orgId = useContextStore((state) => state.org);
  const deploymentId = useContextStore((state) => state.deploymentId);
  const contextLoading = useContextStore((state) => state.isLoading);
  const { loading, isAuthenticated, needsEnrollment } = useAuth();
  const statusCacheRef = useRef<{
    key: string;
    at: number;
    status: StandaloneSetupStatus;
  } | null>(null);
  const hostedConfigCacheRef = useRef<{
    key: string;
    at: number;
    resolved: OrganizationLLMResolvedConfig | null;
  } | null>(null);
  // Durable deterministic jobs and their evidence do not require an LLM.
  const canUseWithoutProvider =
    location.pathname.startsWith("/studio/mcp") ||
    location.pathname.startsWith("/studio/variables") ||
    location.pathname === "/run/travail/activity" ||
    (location.pathname === "/studio/agent-spec" &&
      new URLSearchParams(location.search).get("section") === "executions");

  useEffect(() => {
    if (
      loading ||
      contextLoading ||
      !isAuthenticated ||
      needsEnrollment
    ) {
      return;
    }

    let active = true;

    const bootstrap = async () => {
      const storedAccessMode = getStoredAccessMode();

      try {
        const now = Date.now();
        const cachedStatus = statusCacheRef.current;
        const statusCacheKey = `${orgId || ""}:${deploymentId || ""}`;
        let status: StandaloneSetupStatus;
        if (
          cachedStatus?.key === statusCacheKey &&
          now - cachedStatus.at < 15_000
        ) {
          status = cachedStatus.status;
        } else {
          status = await standaloneSetupAPI.getStatus({
            organizationId: orgId || undefined,
            deploymentId: deploymentId || undefined,
          });
          statusCacheRef.current = { key: statusCacheKey, at: now, status };
        }
        if (!active) {
          return;
        }

        let resolvedLLMConfig: OrganizationLLMResolvedConfig | null = null;
        const effectiveOrganizationId = orgId || status.organizationId || "";
        const effectiveDeploymentId = deploymentId || status.deploymentId || "";
        if (effectiveOrganizationId && effectiveDeploymentId) {
          try {
            const cacheKey = `${effectiveOrganizationId}:${effectiveDeploymentId}`;
            const cachedConfig = hostedConfigCacheRef.current;
            if (cachedConfig?.key === cacheKey && now - cachedConfig.at < 15_000) {
              resolvedLLMConfig = cachedConfig.resolved;
            } else {
              const llmConfig = await llmAPI.getDeploymentConfig(
                effectiveOrganizationId,
                effectiveDeploymentId,
              );
              resolvedLLMConfig = llmConfig.resolved ?? null;
              hostedConfigCacheRef.current = {
                key: cacheKey,
                at: now,
                resolved: resolvedLLMConfig,
              };
            }
          } catch {
            resolvedLLMConfig = null;
          }
        }

        const serverAccessMode = inferAccessModeFromServer(status, resolvedLLMConfig);
        const accessMode = serverAccessMode || storedAccessMode;
        if (serverAccessMode && storedAccessMode !== serverAccessMode) {
          setStoredAccessMode(serverAccessMode);
        } else if (!storedAccessMode && accessMode) {
          setStoredAccessMode(accessMode);
        }

        const needsWelcome = needsStandaloneOnboarding({
          status,
          resolvedLLMConfig,
          storedAccessMode: accessMode,
          organizationId: orgId,
          deploymentId,
          allowMissingProvider: canUseWithoutProvider,
        });

        if (needsWelcome && location.pathname !== "/welcome") {
          navigate("/welcome", { replace: true });
        }
        // Never bounce /welcome → chat automatically. Chat may still have
        // no usable space; "Ouvrir l'accueil" must actually stay on welcome.
      } catch {
        if (!active) {
          return;
        }

        const needsWelcome =
          !storedAccessMode ||
          !orgId ||
          !deploymentId;

        if (
          needsWelcome &&
          !canUseWithoutProvider &&
          location.pathname !== "/welcome"
        ) {
          navigate("/welcome", { replace: true });
        }
      }
    };

    void bootstrap();

    return () => {
      active = false;
    };
  }, [
    contextLoading,
    deploymentId,
    isAuthenticated,
    loading,
    location.pathname,
    location.search,
    navigate,
    needsEnrollment,
    orgId,
    canUseWithoutProvider,
  ]);

  return null;
}

function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

function RedirectWithSection({
  to,
  section,
}: {
  to: string;
  section: string;
}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!params.get("section")) params.set("section", section);
  const search = params.toString();
  return <Navigate to={search ? `${to}?${search}` : to} replace />;
}

function RedirectToAgentSpec({ section }: { section: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("section", section);
  return <Navigate to={`/studio/agent-spec?${params.toString()}`} replace />;
}

function RedirectComposeApplication() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={`/studio/applications/${applicationId || ""}${location.search}`}
      replace
    />
  );
}

function RedirectLegacyRoute() {
  const location = useLocation();
  const target =
    legacyRouteTarget(location.pathname, location.search, location.hash) ||
    "/run/chat";
  return <Navigate to={target} replace />;
}

export default function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const setPosture = useContextStore((state) => state.setPosture);
  const { isAuthenticated, loading, needsEnrollment } = useAuth();
  const { canRead, canWrite, canDeploy, canManageUsers } = usePermissions();
  const canBuildRecipes = canWrite("recipes");
  const canBuildDeploy = canDeploy();
  const canBuildExternalControl = canManageUsers();
  const canAccessBuild = useMemo(
    () =>
      canBuildDeploy ||
      canBuildExternalControl,
    [
      canBuildDeploy,
      canBuildExternalControl,
    ],
  );
  const defaultBuildRoute = useMemo(
    () =>
      resolvePlateformeHomeRoute({
        canBuildDeploy,
        canBuildExternalControl,
        canBuildRecipes,
      }),
    [canBuildDeploy, canBuildExternalControl, canBuildRecipes],
  );
  const buildFallbackHref =
    defaultBuildRoute.startsWith("/studio/") && canAccessBuild
      ? defaultBuildRoute
      : "/run/chat";
  const buildFallbackLabel =
    defaultBuildRoute.startsWith("/studio/") && canAccessBuild
      ? t("build.accessPrimaryCta")
      : t("build.accessBackToRun");
  const buildBackToRunLabel = t("build.accessBackToRun");
  const buildAccessFallback = (
    title: string,
    description: string,
  ) => (
    <AccessBlockedPage
      title={title}
      description={description}
      primaryHref={buildFallbackHref}
      primaryLabel={buildFallbackLabel}
      secondaryHref={
        buildFallbackHref !== "/run/chat" ? "/run/chat" : undefined
      }
      secondaryLabel={
        buildFallbackHref !== "/run/chat"
          ? buildBackToRunLabel
          : undefined
      }
    />
  );

  // Update posture based on current route
  useEffect(() => {
    setPosture(routePosture(location.pathname));
  }, [location.pathname, setPosture]);

  return (
    <NotificationProvider>
      <DesktopUpdateNotifier />
      <AuthGate />
      {!loading && isAuthenticated && needsEnrollment && <EnrollmentScreen />}
      {!loading && isAuthenticated && !needsEnrollment && (
        <>
          <StandaloneWelcomeBootstrap />
          <Suspense
            fallback={
              <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
                {t("auth.loadingApp")}
              </div>
            }
          >
            <Routes>
              <Route path="/welcome" element={<WelcomePage />} />
              <Route element={<Layout />}>
                <Route
                  path="/"
                  element={<RedirectWithSearch to="/run/chat" />}
                />
                <Route
                  path="/run"
                  element={<RedirectWithSearch to="/run/chat" />}
                />
                <Route
                  path="/run/catalog"
                  element={
                    <RequireAccess when={canRead("recipes")}>
                      <CatalogPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/run/chat"
                  element={
                    <RequireAccess when={canRead("executions")}>
                      <ChatPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/run/history"
                  element={
                    <RequireAccess when={canRead("executions")}>
                      <HistoryPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/run/travail/inbox"
                  element={
                    <RequireAccess when={canRead("executions")}>
                      <TravailInboxPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/run/travail/activity"
                  element={
                    <RequireAccess when={canRead("executions")}>
                      <TravailActivityPage />
                    </RequireAccess>
                  }
                />
                <Route path="/run/my-access" element={<MyAccessPage />} />
                <Route
                  path="/run/spaces"
                  element={
                    <RequireAccess when={canRead("executions")}>
                      <RunSpacesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/compose"
                  element={<RedirectWithSearch to="/studio/applications" />}
                />
                <Route
                  path="/compose/:applicationId"
                  element={<RedirectComposeApplication />}
                />
                {/* Former prefixes, kept one release (lot R4). */}
                <Route path="/operate" element={<RedirectLegacyRoute />} />
                <Route path="/operate/*" element={<RedirectLegacyRoute />} />
                <Route path="/build" element={<RedirectLegacyRoute />} />
                <Route path="/build/*" element={<RedirectLegacyRoute />} />
                <Route path="/builder" element={<RedirectLegacyRoute />} />
                <Route path="/builder/*" element={<RedirectLegacyRoute />} />
                <Route
                  path="/studio"
                  element={<RedirectWithSearch to="/studio/chat" />}
                />
                {/* A deep link to the Review never bounces to the chat: while
                    the role is still resolving (or when it is too low) the
                    page explains the access instead (lot R4, OSR-14). */}
                <Route
                  path="/studio/applications"
                  element={
                    <RequireAccess
                      when={canAccessBuild}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <ComposePage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/applications/:applicationId"
                  element={
                    <RequireAccess
                      when={canAccessBuild}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <ComposePage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/chat"
                  element={
                    <RequireAccess
                      when={canBuildRecipes}
                      fallback={buildAccessFallback(
                        t("build.accessRecipesTitle"),
                        t("build.accessRecipesDescription"),
                      )}
                    >
                      <AgentSpecStudioChatPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/agent-spec"
                  element={
                    <RequireAccess
                      when={canBuildRecipes}
                      fallback={buildAccessFallback(
                        t("build.accessRecipesTitle"),
                        t("build.accessRecipesDescription"),
                      )}
                    >
                      <AgentSpecPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/api-keys"
                  element={
                    <RequireAccess
                      when={canBuildDeploy || canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <ApiKeysPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/network-allowlist"
                  element={
                    <RedirectWithSection to="/studio/access" section="network" />
                  }
                />
                <Route
                  path="/studio/external-control"
                  element={<RedirectWithSearch to="/studio/api-keys" />}
                />
                {/* Studio › Organisation (design/os-reorganisation-ecrans § 3, lot R3). */}
                <Route
                  path="/studio/agent-control"
                  element={
                    <RedirectWithSection to="/studio/gates" section="code-agents" />
                  }
                />
                <Route
                  path="/studio/gates"
                  element={
                    <RequireAccess
                      when={canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessExternalControlTitle"),
                        t("build.accessExternalControlDescription"),
                      )}
                    >
                      <GatesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/members"
                  element={
                    <RequireAccess
                      when={canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessExternalControlTitle"),
                        t("build.accessExternalControlDescription"),
                      )}
                    >
                      <MembersPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/access-map"
                  element={
                    <RequireAccess
                      when={canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessExternalControlTitle"),
                        t("build.accessExternalControlDescription"),
                      )}
                    >
                      <AccessMapPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/vault"
                  element={
                    <RequireAccess
                      when={canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessExternalControlTitle"),
                        t("build.accessExternalControlDescription"),
                      )}
                    >
                      <VaultPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/usage"
                  element={
                    <RequireAccess
                      when={canAccessBuild}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <UsagePage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/access"
                  element={
                    <RequireAccess
                      when={canBuildDeploy || canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <AccessPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/import-export"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <ImportExportPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/llm"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <LLMPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/agent-models"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <LLMPage scope="agent" />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/composition"
                  element={<RedirectToAgentSpec section="overview" />}
                />
                <Route
                  path="/studio/mcp"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <MCPPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/connectors"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <IntegrationsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/integrations"
                  element={<RedirectWithSearch to="/studio/connectors" />}
                />
                <Route
                  path="/studio/skills"
                  element={<RedirectWithSection to="/studio/mcp" section="skills" />}
                />
                <Route
                  path="/studio/internal"
                  element={<RedirectWithSearch to="/studio/data" />}
                />
                <Route
                  path="/studio/messaging"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <MessagingPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/mailcatcher"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <MailCatcherPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/spaces"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <SpacesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/variables"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <VariablesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/web"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <ApplicationWebPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/data"
                  element={
                    <RequireAccess
                      when={canBuildRecipes}
                      fallback={buildAccessFallback(
                        t("build.accessRecipesTitle"),
                        t("build.accessRecipesDescription"),
                      )}
                    >
                      <DataPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/schedules"
                  element={<RedirectToAgentSpec section="workflows" />}
                />
                <Route
                  path="*"
                  element={<Navigate to="/run/chat" replace />}
                />
              </Route>
            </Routes>
          </Suspense>
        </>
      )}
    </NotificationProvider>
  );
}
